import {
  Activity,
  Bookmark,
  BookOpen,
  Calculator,
  Compass,
  Heart,
  Leaf,
  Lightbulb,
  Lock,
  Monitor,
  Palette,
} from 'lucide-react';
import type { InteractiveTimetableSlot } from '../TeacherTimetableSection';

interface TimetableSlotCellProps {
  slot: InteractiveTimetableSlot;
  isPastOrCompleted: boolean;
  isDimmed: boolean;
  onClick: () => void;
}

export function TimetableSlotCell({
  slot,
  isPastOrCompleted,
  isDimmed,
  onClick,
}: TimetableSlotCellProps) {
  const subjectLower = (slot.subject || '').toLowerCase();

  // Subject icon and color styling
  let icon = BookOpen;
  let iconColor = 'text-amber-400';

  if (subjectLower.includes('english')) {
    icon = BookOpen;
    iconColor = 'text-amber-400';
  } else if (subjectLower.includes('math')) {
    icon = Calculator;
    iconColor = 'text-emerald-400';
  } else if (subjectLower.includes('evs') || subjectLower.includes('science') || subjectLower.includes('environ')) {
    icon = Leaf;
    iconColor = 'text-blue-400';
  } else if (subjectLower.includes('telugu') || subjectLower.includes('hindi') || subjectLower.includes('tamil')) {
    icon = Bookmark;
    iconColor = 'text-purple-400';
  } else if (subjectLower.includes('art') || subjectLower.includes('craft')) {
    icon = Palette;
    iconColor = 'text-pink-400';
  } else if (subjectLower.includes('computer') || subjectLower.includes('it')) {
    icon = Monitor;
    iconColor = 'text-cyan-400';
  } else if (subjectLower.includes('gk') || subjectLower.includes('general knowledge')) {
    icon = Lightbulb;
    iconColor = 'text-yellow-400';
  } else if (subjectLower.includes('physical') || subjectLower.includes('pe') || subjectLower.includes('sports')) {
    icon = Activity;
    iconColor = 'text-rose-400';
  } else if (subjectLower.includes('value') || subjectLower.includes('moral')) {
    icon = Heart;
    iconColor = 'text-violet-400';
  } else if (subjectLower.includes('social')) {
    icon = Compass;
    iconColor = 'text-orange-400';
  }

  const IconComp = icon;
  const isUnavailable = slot.status === 'UNAVAILABLE';

  return (
    <div
      onClick={onClick}
      style={{ opacity: isDimmed ? 0.25 : 1 }}
      className={`group relative flex items-start gap-2.5 p-2.5 rounded-xl border transition-all cursor-pointer select-none print:p-1.5 print:gap-1.5 print:rounded-lg print:shadow-none print:border-line ${
        isUnavailable
          ? 'bg-rose-500/10 border-rose-500/30 hover:border-rose-500/60'
          : 'bg-surface-2/70 border-line hover:border-indigo-500/50 hover:bg-surface-2'
      } ${isPastOrCompleted ? '' : 'hover:scale-[1.01]'}`}
    >
      {/* Subject Icon */}
      <div className="pt-0.5 shrink-0 print:pt-0">
        <IconComp className={`h-4 w-4 ${iconColor} print:h-3.5 print:w-3.5`} />
      </div>

      {/* Subject & Teacher Name */}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="truncate text-sm font-bold text-ink leading-tight print:text-[11px] print:leading-tight">
          {slot.subject}
        </span>
        {/* The class it is taught to. This is the teacher's own timetable, so
            naming a teacher here only ever repeated the reader — and with no
            name to hand it printed a made-up one. */}
        {slot.className && (
          <span className="truncate text-[11px] font-medium text-ink-3 mt-0.5 print:mt-0 print:text-[9px]">
            {slot.className}
          </span>
        )}

        {isUnavailable && (
          <span className="text-[10px] font-bold text-rose-500 truncate mt-0.5 print:text-[8px] print:mt-0">
            {slot.leaveCategory || 'On Leave'}
          </span>
        )}
      </div>

      {/* Lock icon for completed periods */}
      {isPastOrCompleted && (
        <span className="shrink-0 p-0.5 text-ink-3/60 print:hidden" title="Completed period">
          <Lock className="h-3 w-3" />
        </span>
      )}
    </div>
  );
}
